import type { ReactNode } from "react";
import { OverlayTrigger } from "react-bootstrap";
import { AiOutlineQuestionCircle } from "react-icons/ai";
import { RenderTooltip } from "../../../../../components/common/RenderTooltip";
import TextCopy from "../../../../../components/common/TextCopy";
import type { ConnectionHubError } from "../../../api/connectionHub.types";
import type { ProviderSetupGuide } from "../../../constants/providerSetupGuides.constants";
import ConnectionErrorPanel from "../../common/ConnectionErrorPanel/ConnectionErrorPanel";
import PhaseActions from "../PhaseActions/PhaseActions";
import StepsAccordion from "../StepsAccordion/StepsAccordion";
import styles from "./ForwardingPhasePanel.module.scss";

export interface ForwardingPhasePanelProps {
  /** Takes the instruction's place: what to do next is the panel's own second line. */
  error?: ConnectionHubError | string | null;
  description?: string;
  /** Show the error above the instruction instead of in its place (Gmail's forwarding check failed). */
  keepInstruction?: boolean;
  caption?: string;
  forwardingAddress?: string | null;
  guide?: ProviderSetupGuide;
  settingsUrl: string | null;
  settingsLabel?: string;
  confirmLabel?: string;
  declineLabel?: string;
  /** Named on the "this will open X as …" step before the settings are opened. */
  providerName: string;
  accountEmail: string;
  /** Sends the step sequence back to the start when the inbox moves on. */
  resetKey?: string;
  appearance?: "default" | "primary";
  confirmDismissible?: boolean;
  confirmBeforeOpen?: boolean;
  /** Passed to PhaseActions: the delegate page's Back, on the button row. */
  leading?: ReactNode;
  canOpen?: boolean;
  canCopy?: boolean;
  canConfirm?: boolean;
  disabled?: boolean;
  busy?: boolean;
  onOpen: () => void;
  onConfirm: () => void;
}

const ForwardingPhasePanel = ({ error, description, keepInstruction = false, caption = "Merito's official, verified address, used only to receive OTPs for this connection.", forwardingAddress, guide, settingsUrl, settingsLabel = "Open Forwarding Settings", confirmLabel = "I’ve Set Up Forwarding", declineLabel = "I’ve Not Set Up Forwarding", providerName, accountEmail, resetKey, appearance = "default", confirmDismissible = false, confirmBeforeOpen = false, leading, canOpen = true, canCopy = false, canConfirm = true, disabled = false, busy = false, onOpen, onConfirm }: ForwardingPhasePanelProps): JSX.Element => {
  const instruction = error && !keepInstruction ? null : forwardingAddress ? (
    <p className={`${styles.sentence} ${styles.singleLine}`}>
      <span>Click on the button and you’ll add</span>
      <TextCopy iconOnlyFeedback text={forwardingAddress} ariaLabel="Copy Merito forwarding address" className={styles.addressChip} />
      <span>as a forwarding address to your inbox settings.</span>
      <OverlayTrigger placement="top" trigger={["hover", "focus"]} overlay={(props) => RenderTooltip(props, caption)}>
        <button type="button" className={styles.help} aria-label={caption}>
          <AiOutlineQuestionCircle aria-hidden="true" />
        </button>
      </OverlayTrigger>
    </p>
  ) : description ? <p className={styles.sentence}>{description}</p> : null;

  return (
    <div className={styles.panel}>
      {error ? <ConnectionErrorPanel error={error} /> : null}
      {instruction || guide ? (
        <div className={styles.guidance}>
          {instruction}
          {guide ? <StepsAccordion steps={guide.forwarding} {...(guide.notice ? { notice: guide.notice } : {})} videoUrl={guide.forwardingVideoUrl} phase="forwarding" providerName={guide.displayName} forwardingAddress={forwardingAddress} /> : null}
        </div>
      ) : null}
      <PhaseActions
        providerName={providerName}
        accountEmail={accountEmail}
        settingsUrl={settingsUrl}
        settingsLabel={settingsLabel}
        confirmLabel={confirmLabel}
        declineLabel={declineLabel}
        appearance={appearance}
        confirmDismissible={confirmDismissible}
        confirmBeforeOpen={confirmBeforeOpen}
        leading={leading}
        canOpen={canOpen}
        canCopy={canCopy}
        canConfirm={canConfirm}
        disabled={disabled}
        busy={busy}
        {...(resetKey ? { resetKey } : {})}
        onOpen={onOpen}
        onConfirm={onConfirm}
      />
    </div>
  );
};

export default ForwardingPhasePanel;
